import { ChatAltIcon } from "@heroicons/react/outline";
import { findIndex, isEmpty, orderBy } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Link,
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import Hex from "../../../components/Hex";
import ChevronDownIcon from "../../../components/icons/ChevronDownIcon";
import ChevronUpIcon from "../../../components/icons/ChevronUpIcon";
import CloseIcon from "../../../components/icons/CloseIcon";
import NavLink from "../../../components/NavLink";
import { useDiscordChannels } from "../../../context/discordChannels";
import { useTransmitScales } from "../../../context/transmitScales";
import {
  classificationColorMap,
  getClassification
} from "../../../utils/hotspotUtils";
import { h3ToGeoMemoized } from "../../../utils/memoizedMethods";
import useMapCenter from "../../../utils/useMapCenter";
import HexDiscordTab from "./HexDiscordTab";
import HexTransmitScaleTab from "./HexTransmitScaleTab";

const HexDetails: React.FC<RouteComponentProps<{ h3Index: string }>> = (
  props
) => {
  const setCenter = useMapCenter((state) => state.setCenter);
  const setZoom = useMapCenter((state) => state.setZoom);
  const {
    calculatedTransmitScales,
    problematicHexes,
    getHexTransmitScaleInfo
  } = useTransmitScales();
  const lastCenteredH3Index = useRef<string>();
  const lastZoomedH3Index = useRef<string>();
  const { channels } = useDiscordChannels();
  const { resolution, transmitScale } = useMemo(
    () => getHexTransmitScaleInfo(props.match.params.h3Index),
    [getHexTransmitScaleInfo, props.match.params.h3Index]
  );

  const orderedProblematicHexes = useMemo(
    () =>
      orderBy(
        problematicHexes.map((h3Index) => getHexTransmitScaleInfo(h3Index)),
        "transmitScale",
        "desc"
      ),
    [getHexTransmitScaleInfo, problematicHexes]
  );

  const detailsPageMatch = matchPath<{
    h3Index?: string;
    section?: string;
    subsection?: string;
  }>(props.location.pathname, {
    path: ["/hexes/:h3Index/:section/:subsection", "/hexes/:h3Index/:section"]
  });

  const zoomMapOnHex = useCallback(() => {
    const newZoom = zoomByHexResolution[resolution];

    if (newZoom && lastZoomedH3Index.current !== props.match.params.h3Index) {
      setZoom([newZoom]);
      lastZoomedH3Index.current = props.match.params.h3Index;
    }
  }, [props.match.params.h3Index, resolution, setZoom]);

  const centerMapOnHex = useCallback(() => {
    const [latitude, longitude] = h3ToGeoMemoized(props.match.params.h3Index);

    if (lastCenteredH3Index.current !== props.match.params.h3Index) {
      setCenter([longitude, latitude]);
      lastCenteredH3Index.current = props.match.params.h3Index;
    }
  }, [props.match.params.h3Index, setCenter]);

  const focusMapOnHex = useCallback(() => {
    zoomMapOnHex();
    centerMapOnHex();
  }, [centerMapOnHex, zoomMapOnHex]);

  useEffect(() => {
    focusMapOnHex();
  }, [focusMapOnHex]);

  const renderPagination = useMemo(
    () => (
      <nav className="flex" aria-label="Pagination">
        <span className="relative z-0 shadow-sm rounded-md flex justify-between items-center">
          <button
            type="button"
            className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-500 focus:border-hotspotty-500"
            onClick={() => {
              if (isEmpty(orderedProblematicHexes)) return;

              const {
                h3Index,
                section,
                subsection
              }: {
                h3Index?: string;
                section?: string;
                subsection?: string;
              } = detailsPageMatch?.params || {};

              const currentSelectedHexIndex = findIndex(
                orderedProblematicHexes,
                {
                  h3Index
                }
              );

              const newHexIndex =
                currentSelectedHexIndex > 0
                  ? currentSelectedHexIndex - 1
                  : orderedProblematicHexes.length - 1;

              let sectionPath = "transmit-scale";
              if (section) {
                sectionPath = subsection ? `${section}/${subsection}` : section;
              }

              props.history.push(
                `/hexes/${orderedProblematicHexes[newHexIndex].h3Index}/${sectionPath}${props.location.search}`
              );
            }}
          >
            <span className="sr-only">Previous</span>
            <ChevronUpIcon className="h-5 w-5" />
          </button>

          <button
            type="button"
            className={`-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-500 focus:border-hotspotty-500 ${
              props.match.params.h3Index ? "" : "rounded-r-md"
            }`}
            onClick={() => {
              if (isEmpty(orderedProblematicHexes)) return;

              const {
                h3Index,
                section,
                subsection
              }: {
                h3Index?: string;
                section?: string;
                subsection?: string;
              } = detailsPageMatch?.params || {};

              const currentSelectedHexIndex = findIndex(
                orderedProblematicHexes,
                {
                  h3Index
                }
              );

              const newHexIndex =
                currentSelectedHexIndex < orderedProblematicHexes.length - 1
                  ? currentSelectedHexIndex + 1
                  : 0;

              let sectionPath = "transmit-scale";
              if (section) {
                sectionPath = subsection ? `${section}/${subsection}` : section;
              }

              props.history.push(
                `/hexes/${orderedProblematicHexes[newHexIndex].h3Index}/${sectionPath}${props.location.search}`
              );
            }}
          >
            <span className="sr-only">Next</span>
            <ChevronDownIcon className="h-5 w-5" />
          </button>

          <Link
            to="/hexes"
            className="-ml-px relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-500 focus:border-hotspotty-500"
          >
            <CloseIcon className="h-5 w-5" />
          </Link>
        </span>
      </nav>
    ),
    [
      detailsPageMatch?.params,
      orderedProblematicHexes,
      props.history,
      props.location.search,
      props.match.params.h3Index
    ]
  );

  const tabs = useMemo(
    () => [
      { key: "transmit-scale", title: "Transmit scale" },
      {
        key: "discord",
        title: "Discord collaboration",
        Icon: !!channels[props.match.params.h3Index] ? (
          <ChatAltIcon className="mr-2 inline-flex h-5 w-5 text-blue-500" />
        ) : null
      }
    ],
    [channels, props.match.params.h3Index]
  );

  if (isEmpty(calculatedTransmitScales)) {
    return <CenteredLoading className="h-full" />;
  }

  return (
    <article className="pb-6">
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16">
            <div className="flex justify-between">
              <Tooltip
                overlay={`Click to center the map on this resolution ${resolution} hex`}
              >
                <div
                  className="z-10 relative cursor-pointer"
                  onClick={() => {
                    lastZoomedH3Index.current = undefined;
                    lastCenteredH3Index.current = undefined;
                    focusMapOnHex();
                  }}
                >
                  <Hex
                    fillColor={
                      (classificationColorMap as any)[
                        getClassification(transmitScale)
                      ]
                    }
                    width={128}
                    height={128}
                    className="h-24 w-24 sm:h-32 sm:w-32"
                  />
                  <div className="absolute inset-0 flex justify-center items-center">
                    <div className="text-4xl sm:text-6xl text-white">
                      {resolution}
                    </div>
                  </div>
                </div>
              </Tooltip>
              <div className="flex items-center">{renderPagination}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-2 2xl:mt-5">
        <div className="sm:border-b sm:border-gray-200 dark:border-gray-500 overflow-auto whitespace-nowrap">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <select
              className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
              onChange={(e) => {
                props.history.push(`${props.match.url}/${e.target.value}`);
              }}
              value={detailsPageMatch?.params.section}
            >
              {tabs.map(({ key, title }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </select>
            <div className="hidden sm:block">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map(({ key, title, Icon }) => (
                  <NavLink
                    key={key}
                    to={`${props.match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-gray-900 dark:text-white dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center"
                  >
                    {Icon}
                    {title}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Switch>
        <Route
          path={`${props.match.path}/transmit-scale`}
          component={HexTransmitScaleTab}
        />
        <Route path={`${props.match.path}/discord`} component={HexDiscordTab} />
        <Redirect to={`${props.match.path}/transmit-scale`} />
      </Switch>
    </article>
  );
};

export default HexDetails;

//
// Utils
//

const zoomByHexResolution: { [resolution: number]: number } = {
  10: 14,
  9: 14,
  8: 13,
  7: 12,
  6: 11,
  5: 10,
  4: 9
};
